import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';

// Categories the AI prompts pull from. Adding a category here makes it appear
// in the picker; the backend currently injects 'sow_generation', 'sow_recommendations',
// and 'sow_examples' into SOW prompts.
const CATEGORIES = [
  { value: 'sow_generation',      label: 'SOW Generation',      description: 'House style, mandatory sections, banned phrases, tone — used when Claude drafts a SOW.' },
  { value: 'sow_recommendations', label: 'SOW Recommendations', description: 'Rules for how to recommend resources, hardware, milestones from the SOR.' },
  { value: 'sow_examples',        label: 'SOW Examples',        description: 'Snippets / paragraphs from past good SOWs for the AI to imitate.' },
];

export default function AiKnowledgePage() {
  const { toast } = useToast();
  const [notes, setNotes]     = useState([]);
  const [active, setActive]   = useState(CATEGORIES[0].value);
  const [editing, setEditing] = useState(null);  // null | 'new' | note
  const [saving, setSaving]   = useState(false);

  const [corpus, setCorpus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    api.get('ai-knowledge')
      .then(data => setNotes(Array.isArray(data) ? data : []))
      .catch(() => setNotes([]));
    api.get('sow-reference-cache').then(setCorpus).catch(() => setCorpus(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshCorpus = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('sow-reference-cache/refresh', {});
      const errs = [r.sharepoint_error, r.past_sow_error].filter(Boolean);
      const counts = `${r.sharepoint} SharePoint doc${r.sharepoint === 1 ? '' : 's'} + ${r.past_sows} past SOW${r.past_sows === 1 ? '' : 's'}`;
      // Surface the real reason when a run captures nothing, instead of a silent "0".
      if (errs.length) toast.error(`Reference corpus: ${counts}. ${errs.join(' · ')}`);
      else toast.success(`Reference corpus refreshed: ${counts}`);
      api.get('sow-reference-cache').then(setCorpus).catch(() => {});
    } catch (e) { toast.error('Refresh failed: ' + e.message); }
    setRefreshing(false);
  };

  const filtered = notes.filter(n => n.category === active);
  const activeMeta = CATEGORIES.find(c => c.value === active) || { label: active };

  const save = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`ai-knowledge/${form.id}`, form);
      } else {
        await api.post('ai-knowledge', { ...form, category: active });
      }
      load();
      setEditing(null);
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const remove = async (note) => {
    if (!confirm(`Delete note "${note.title || note.content.slice(0, 40)}"?`)) return;
    await api.delete(`ai-knowledge/${note.id}`);
    load();
  };

  const toggleActive = async (note) => {
    await api.put(`ai-knowledge/${note.id}`, { is_active: note.is_active ? 0 : 1 });
    load();
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">AI Knowledge</h2>
          <div className="text-[9px] text-gray-500">Running notes the AI pulls into its prompts. Update these as you learn what the AI gets wrong — improvements stick on every future generation.</div>
        </div>
        <button onClick={() => setEditing('new')} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ New Note</button>
      </div>

      {/* Reference corpus — the stored "memory" the SOW AI draws on */}
      <div className="bg-white border border-gray-200 rounded p-3 mb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold text-navy">Reference corpus (AI memory)</div>
            <div className="text-[8px] text-gray-500 mt-0.5">
              A stored snapshot of the SharePoint Claude_CoWork archive and previously generated SOWs. The SOW generator reads from this (sent with prompt caching to keep token cost low) instead of re-downloading documents on every run. Auto-refreshes weekly.
            </div>
            {corpus && (
              <div className="text-[8px] text-gray-600 mt-1.5 flex gap-3 flex-wrap">
                <span><strong>{corpus.sharepoint || 0}</strong> SharePoint docs</span>
                <span><strong>{corpus.past_sows || 0}</strong> past SOWs</span>
                <span>~{Math.round((corpus.total_chars || 0) / 1000)}k chars</span>
                <span className="text-gray-400">Last refreshed: {corpus.refreshed_at ? new Date(corpus.refreshed_at + 'Z').toLocaleString('en-GB') : 'never'}</span>
              </div>
            )}
            {/* Diagnostics from the last run — explains an empty corpus instead of a silent 0 */}
            {corpus?.last_run && (corpus.last_run.sharepoint_error || corpus.last_run.past_sow_error ||
              (corpus.last_run.past_sow_candidates === 0)) && (
              <div className="mt-1.5 space-y-0.5">
                {corpus.last_run.sharepoint_error && (
                  <div className="text-[8px] text-red-600"><strong>SharePoint:</strong> {corpus.last_run.sharepoint_error}</div>
                )}
                {corpus.last_run.past_sow_error && (
                  <div className="text-[8px] text-red-600"><strong>Past SOWs:</strong> {corpus.last_run.past_sow_error}</div>
                )}
                {!corpus.last_run.past_sow_error && corpus.last_run.past_sow_candidates === 0 && (
                  <div className="text-[8px] text-amber-600">No past-SOW documents to learn from yet — generate or issue a SOW to seed examples.</div>
                )}
                {!corpus.last_run.past_sow_error && corpus.last_run.past_sow_candidates > 0 && corpus.last_run.past_sows === 0 && (
                  <div className="text-[8px] text-amber-600">Found {corpus.last_run.past_sow_candidates} SOW document(s) but none yielded readable text (likely scanned/image PDFs).</div>
                )}
              </div>
            )}
          </div>
          <button onClick={refreshCorpus} disabled={refreshing}
            className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-40 whitespace-nowrap">
            {refreshing ? 'Refreshing…' : '↻ Refresh now'}
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 mb-3 border-b border-gray-200">
        {CATEGORIES.map(c => {
          const count = notes.filter(n => n.category === c.value && n.is_active).length;
          return (
            <button key={c.value} onClick={() => setActive(c.value)}
              className={`px-3 py-1.5 text-[10px] font-semibold border-b-2 -mb-px ${active === c.value ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {c.label} <span className="ml-1 text-[8px] text-gray-400">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="text-[8px] text-gray-500 mb-3 italic">{activeMeta.description}</div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">
          No notes in <strong>{activeMeta.label}</strong> yet.<br />
          <button onClick={() => setEditing('new')} className="mt-2 px-2 py-1 text-[9px] bg-navy text-white rounded">Add the first note</button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(n => (
            <div key={n.id} className={`bg-white border rounded p-3 ${n.is_active ? 'border-gray-200' : 'border-gray-200 opacity-50'}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex-1">
                  <div className="text-[10px] font-bold text-navy">{n.title || <span className="text-gray-400 italic">Untitled note</span>}</div>
                  <div className="text-[7.5px] text-gray-400">
                    Updated {n.updated_at ? new Date(n.updated_at).toLocaleString('en-GB') : '—'} · by {n.created_by || 'unknown'}
                    {n.source && n.source !== 'admin' && <span className="ml-1 px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-[7px] font-bold">{n.source}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => toggleActive(n)} className={`px-2 py-0.5 text-[8px] rounded ${n.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {n.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button onClick={() => setEditing(n)} className="px-2 py-0.5 text-[8px] bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Edit</button>
                  <button onClick={() => remove(n)} className="px-2 py-0.5 text-[8px] bg-red-100 text-red-600 rounded hover:bg-red-200">×</button>
                </div>
              </div>
              <pre className="text-[9px] text-gray-700 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">{n.content}</pre>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <NoteEditorModal
          note={editing === 'new' ? { category: active, title: '', content: '' } : editing}
          categories={CATEGORIES}
          onClose={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function NoteEditorModal({ note, categories, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    id: note.id || null,
    category: note.category || categories[0].value,
    title: note.title || '',
    content: note.content || '',
    is_active: note.is_active ?? 1,
  });
  return (
    <Modal title={note.id ? `Edit Note` : 'New AI Knowledge Note'} onClose={onClose} wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.content.trim()} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
            {saving ? 'Saving…' : note.id ? 'Save Changes' : 'Create'}
          </button>
        </>
      }>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Category</label>
            <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
              {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Title (optional)</label>
            <input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Don't say 'leverage'" />
          </div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Content (Markdown)</label>
          <textarea
            className="form-input font-mono text-[9px] h-72 resize-y"
            value={form.content}
            onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
            autoFocus
            placeholder="Write the rule, example or guidance. Markdown is supported.&#10;&#10;e.g.&#10;## Tone&#10;- Use plain English. No 'leverage', no 'synergy'.&#10;- Always quote prices in £ GBP.&#10;&#10;## Mandatory sections&#10;- Acceptance Criteria must list testable conditions, not vague outcomes."
          />
          <div className="text-[7.5px] text-gray-400 mt-1">This text is appended to the AI system prompt for any matching category — keep it focused and high-signal.</div>
        </div>
      </div>
    </Modal>
  );
}
